/**
 * Render Helpers Module
 * Utility functions for render-time button state management and calculations
 */

import type { Lot } from '../types';
import { isLotClosed } from './modules/utils';
import { ensureSettings } from './modules/state';
import { VIEW_MODES } from './modules/state';
import { getEffectiveCloseTime } from './modules/storage';
import { hasPricingData } from './modules/rendering';
import { isDeadMeat } from './modules/lotOperations/financials';

/**
 * Calculate the number of "dead meat" lots (negative margin on both low and high estimates)
 */
export function calculateDeadMeatCount(lots: Lot[]): number {
  if (!Array.isArray(lots)) return 0;

  const settings = ensureSettings();
  return lots.reduce((count: number, lot: Lot) => {
    return count + (isDeadMeat(lot, settings) ? 1 : 0);
  }, 0);
}

/**
 * Calculate the number of closed lots
 */
export function calculateClosedLotsCount(lots: Lot[]): number {
  if (!Array.isArray(lots)) return 0;

  return lots.reduce(
    (count: number, lot: any) => count + (isLotClosed(lot, getEffectiveCloseTime) ? 1 : 0),
    0
  );
}

/**
 * Update the "Remove Closed Lots" button state
 */
export function updateRemoveClosedLotsButton(
  el: HTMLElement | null,
  lots: Lot[],
  isBusy: boolean
): void {
  if (!el) return;

  const closedCount = calculateClosedLotsCount(lots);
  const hasClosedLots = closedCount > 0;

  el.hidden = !hasClosedLots;
  (el as HTMLButtonElement).disabled = isBusy;
  el.textContent = isBusy ? 'Removing closed lots…' : 'Remove Closed Lots';

  if (hasClosedLots) {
    const ariaLabel =
      closedCount === 1 ? 'Remove 1 closed lot' : `Remove ${closedCount} closed lots`;
    el.setAttribute('aria-label', ariaLabel);
  } else {
    el.removeAttribute('aria-label');
  }
}

/**
 * Update the "Remove Dead Meat" button state
 */
export function updateRemoveDeadMeatButton(
  el: HTMLElement | null,
  lots: Lot[],
  isBusy: boolean
): void {
  if (!el) return;

  const deadMeatCount = calculateDeadMeatCount(lots);
  const hasDeadMeat = deadMeatCount > 0;

  el.hidden = !hasDeadMeat;
  (el as HTMLButtonElement).disabled = isBusy;
  el.textContent = isBusy ? 'Removing dead meat…' : 'Remove Dead Meat';

  if (hasDeadMeat) {
    const ariaLabel =
      deadMeatCount === 1
        ? 'Remove 1 unprofitable lot'
        : `Remove ${deadMeatCount} unprofitable lots`;
    el.setAttribute('aria-label', ariaLabel);
  } else {
    el.removeAttribute('aria-label');
  }
}

/**
 * Update the "Archive URLs" button visibility based on view mode
 */
export function updateArchiveUrlsButton(el: HTMLElement | null, viewMode: string): void {
  if (!el) return;
  el.hidden = viewMode !== VIEW_MODES.SUPERADMIN;
}

/**
 * Calculate lot statistics for rendering
 */
export interface LotStatistics {
  pricedCount: number;
  aiPendingCount: number;
  retryCount: number;
}

export function calculateLotStatistics(lots: Lot[]): LotStatistics {
  const pricedCount = lots.reduce(
    (count: number, lot: any) => count + (hasPricingData(lot) ? 1 : 0),
    0
  );

  const aiPendingCount = lots.reduce((count: number, lot: Lot) => {
    if (!lot) return count;
    const status = lot.aiStatus;
    return count + (status === 'processing' || status === 'pending' ? 1 : 0);
  }, 0);

  const retryCount = lots.reduce((count: number, lot: Lot) => {
    if (!lot || !lot.priceReview) return count;
    return count + (lot.priceReview.status === 'failedPrice' ? 1 : 0);
  }, 0);

  return { pricedCount, aiPendingCount, retryCount };
}
